import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { ReactComponent as ArrowLeft } from '../../public/images/svg/Arrowhead-Left-01.svg';
import { ReactComponent as ArrowRight } from '../../public/images/svg/Arrowhead-Right-01.svg';
import './css/RegionScroller.css';

interface RegionWindowChangeProps {
    leftPx: number;
    scrollOffsetPx: number;
}

export interface RegionWindowSizeChangeProps {
    widthPx: number;
}

interface RegionScrollerProps {
    regionWidthPx?: number;
    regionWindowPositionTitle: string | null;
    regionWindowInteriorTitle: string | null;
    children: React.ReactElement;
    initialRegionWindowLeftPx?: number;
    initialScrollLeftPx?: number;
    regionMinWidthPx: number;
    regionMaxWidthPx: number;
    onWidthChange?: (width: number) => void;
    regionWindowChange?: (result: RegionWindowChangeProps) => void;
    regionWindowChanged?: (result: RegionWindowChangeProps) => void;
    regionWindowSizeChange?: (result: RegionWindowSizeChangeProps) => void;
    regionWindowSizeChanged?: (result: RegionWindowSizeChangeProps) => void;
}

export const RegionScroller: React.FC<RegionScrollerProps> = (
    {
        regionWidthPx,
        regionWindowPositionTitle,
        regionWindowInteriorTitle,
        children,
        initialRegionWindowLeftPx = 0,
        initialScrollLeftPx = 0,
        regionMinWidthPx,
        regionMaxWidthPx,
        onWidthChange,
        regionWindowChange,
        regionWindowChanged,
        regionWindowSizeChange,
        regionWindowSizeChanged
    }) => {
    
    const contentUndershotAreaRef = useRef<HTMLDivElement | null>(null);
    const contentOvershotAreaRef = useRef<HTMLDivElement | null>(null);
    const contentContainerRef = useRef<HTMLDivElement | null>(null);
    const regionWindowRef = useRef<HTMLDivElement | null>(null);
    const regionWindowSizerRef = useRef<HTMLDivElement | null>(null);
    const [regionWindowWidth, setRegionWindowWidth] = useState<number | null>(regionWidthPx ? regionWidthPx : 0);
    const regionWindowWidthRef = useRef<number | null>(regionWidthPx ? regionWidthPx : 0);
    const regionWindowMinWidthRef = useRef<number | null>(regionMinWidthPx);
    const regionWindowMaxWidthRef = useRef<number | null>(regionMaxWidthPx);
    const [regionWindowLeft, setRegionWindowLeft] = useState<number | null>(null);
    const [regionScrollerWidth, setRegionScrollerWidth] = useState<number | null>(null);

    const [isOvershooting, setIsOvershooting] = useState(false);
    const overshootRef = useRef<number | null>(null);
    const overshootDistanceRef = useRef(0);

    const minRegionWindowXRef = useRef<number | null>(null);
    const maxRegionWindowXRef = useRef<number | null>(null);

    let isDragging = false;
    let startX = 0;
    let startLeft = 0;

    let isResizing = false;
    let resizeStartX = 0;
    let startWidth = 0;

    useEffect(() => {
        regionWindowWidthRef.current = regionWindowWidth;
        regionWindowMinWidthRef.current = regionMinWidthPx;
        regionWindowMaxWidthRef.current = regionMaxWidthPx;
    }, [regionWindowWidth, regionMinWidthPx, regionMaxWidthPx]);

    const startWindowChangeMotion = () => {
        if (overshootRef.current !== null) return; // already running
        const tick = () => {
            // Move the content container scroll
            if (contentContainerRef.current) {
                const newScrollPosition = contentContainerRef.current.scrollLeft + overshootDistanceRef.current;
                contentContainerRef.current.scrollTo(newScrollPosition, 0);
                regionWindowChange?.({ leftPx: regionWindowLeft ? regionWindowLeft : 0, scrollOffsetPx: newScrollPosition });
            }
            overshootRef.current = requestAnimationFrame(tick);
        };
        overshootRef.current = requestAnimationFrame(tick);
    };

    const stopWindowChangeMotion = () => {
        if (overshootRef.current !== null) {
            cancelAnimationFrame(overshootRef.current);
            overshootRef.current = null;
        }
    };

    useEffect(() => {
        if (isOvershooting) {
            startWindowChangeMotion();
        } else {
            stopWindowChangeMotion();
        }
    }, [isOvershooting]);

    function observeElementWidth(
        el: HTMLElement | null,
        setWidth: Dispatch<SetStateAction<number>> | ((width: number) => void)
    ) {
        if (!el) return;

        const measure = () => {
            const rect = el.getBoundingClientRect();
            setWidth(rect.width);
            onWidthChange?.(rect.width);
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);

        return () => observer.disconnect();
    }

    const onPointerDown = (e: PointerEvent) => {
        isDragging = true;
        startX = e.clientX;
        startLeft = regionWindowRef.current!.offsetLeft;
        regionWindowRef.current!.setPointerCapture(e.pointerId);
        e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
        if (!isDragging || !regionWindowRef.current || startLeft === null) return;

        const dx = e.clientX - startX;
        const newLeft = startLeft + dx;

        setRegionWindowLeft(newLeft);

        if (minRegionWindowXRef.current && newLeft < minRegionWindowXRef.current) {
            overshootDistanceRef.current = newLeft - minRegionWindowXRef.current;
            setIsOvershooting(true);

        } else if (maxRegionWindowXRef.current && newLeft > maxRegionWindowXRef.current) {
            overshootDistanceRef.current = newLeft - maxRegionWindowXRef.current;
            setIsOvershooting(true);
        }
        else {
            setIsOvershooting(false);
        }

        if (contentContainerRef.current && minRegionWindowXRef.current) {
            const trimmedLeft = newLeft - minRegionWindowXRef.current > 0 ? newLeft - minRegionWindowXRef.current : 0;
            regionWindowChange?.({ leftPx: trimmedLeft, scrollOffsetPx: contentContainerRef.current?.scrollLeft });
        }
    };

    const onPointerUp = (e: PointerEvent) => {
        isDragging = false;

        if (!regionWindowRef.current || startLeft === null) return;

        regionWindowRef.current?.releasePointerCapture(e.pointerId);

        const dx = e.clientX - startX;
        const newLeft = startLeft + dx;

        if (minRegionWindowXRef.current && newLeft < minRegionWindowXRef.current) {
            setRegionWindowLeft(minRegionWindowXRef.current);
        }
        else if (maxRegionWindowXRef.current && newLeft > maxRegionWindowXRef.current) {
            setRegionWindowLeft(maxRegionWindowXRef.current);            
        }

        setIsOvershooting(false);

        if (contentContainerRef.current && minRegionWindowXRef.current) {
            const trimmedLeft = newLeft - minRegionWindowXRef.current > 0 ? newLeft - minRegionWindowXRef.current : 0;

            regionWindowChanged?.({ leftPx: trimmedLeft, scrollOffsetPx: contentContainerRef.current?.scrollLeft });
        }
    };

    const onContentContainerPointerDown = (e: PointerEvent) => {
        if (contentContainerRef.current && regionWindowWidthRef.current) {

            const pointerX = contentContainerRef.current.scrollLeft + e.offsetX; 
            const newRegionLeftX = pointerX - (regionWindowWidthRef.current / 2);

            setRegionWindowLeft(newRegionLeftX - contentContainerRef.current.scrollLeft);

            regionWindowChanged?.({ leftPx: newRegionLeftX, scrollOffsetPx: contentContainerRef.current?.scrollLeft });
        }
    }


    const onOvershotPointerDown = (e: PointerEvent) => {
        overshootDistanceRef.current = 10;
        setIsOvershooting(true);
    }

    const onOvershotPointerUp = (e: PointerEvent) => {
        setIsOvershooting(false);
    }

    const onUndershotPointerDown = (e: PointerEvent) => {
        overshootDistanceRef.current = -10;
        setIsOvershooting(true);
    }

    const onUndershotPointerUp = (e: PointerEvent) => {
        setIsOvershooting(false);
    }

    const regionWindowSizerPointerDown = (e: PointerEvent) => {
        if (!isResizeEnabled) return;

        isResizing = true;
        resizeStartX = e.clientX;
        startWidth = regionWindowWidthRef.current ? regionWindowWidthRef.current : 0;

        regionWindowSizerRef.current!.setPointerCapture(e.pointerId);
        e.preventDefault();
        e.stopPropagation();
    }

    const regionWindowSizerPointerMove = (e: PointerEvent) => {
        if (!isResizing || !isResizeEnabled || !regionWindowSizerRef.current || !regionWindowMinWidthRef.current || !regionWindowMaxWidthRef.current) return;

        const dx = e.clientX - resizeStartX;

        const newWidth = startWidth ? startWidth + dx : dx;

        let constrainedWidth = newWidth <= regionWindowMaxWidthRef.current ? newWidth : regionWindowMaxWidthRef.current;
        constrainedWidth = constrainedWidth >= regionWindowMinWidthRef.current ? constrainedWidth : regionWindowMinWidthRef.current;

        setRegionWindowWidth(constrainedWidth);
        regionWindowSizeChange?.({ widthPx: constrainedWidth })

        e.preventDefault();
        e.stopPropagation();
    }

    const regionWindowSizerPointerUp = (e: PointerEvent) => {
        if (!isResizing || !isResizeEnabled || !regionWindowSizerRef.current) return;

        isResizing = false;
        const newWidth = regionWindowWidthRef.current!;
        regionWindowSizeChanged?.({ widthPx: newWidth });

        e.preventDefault();
        e.stopPropagation();
    }

    const isResizeEnabled = true;

    useEffect(() => {
        if (regionWindowRef.current && regionWindowSizerRef.current && contentContainerRef.current
            && contentOvershotAreaRef.current && contentUndershotAreaRef.current) {
            regionWindowRef.current.addEventListener("pointerdown", onPointerDown);
            regionWindowRef.current.addEventListener("pointermove", onPointerMove);
            regionWindowRef.current.addEventListener("pointerup", onPointerUp);

            regionWindowSizerRef.current.addEventListener("pointerdown", regionWindowSizerPointerDown);
            regionWindowSizerRef.current.addEventListener("pointermove", regionWindowSizerPointerMove);
            regionWindowSizerRef.current.addEventListener("pointerup", regionWindowSizerPointerUp);

            contentOvershotAreaRef.current.addEventListener("pointerdown", onOvershotPointerDown);
            contentOvershotAreaRef.current.addEventListener("pointerup", onOvershotPointerUp);
            contentUndershotAreaRef.current.addEventListener("pointerdown", onUndershotPointerDown);
            contentUndershotAreaRef.current.addEventListener("pointerup", onUndershotPointerUp);

            contentContainerRef.current.addEventListener("pointerdown", onContentContainerPointerDown);

            observeElementWidth(contentContainerRef.current, setRegionScrollerWidth);

            const cleanupResizeObserver = observeElementWidth(
                contentContainerRef.current,
                setRegionScrollerWidth
            );

            return () => {
                if (regionWindowRef.current && regionWindowSizerRef.current && contentContainerRef.current
                    && contentOvershotAreaRef.current && contentUndershotAreaRef.current) {
                    regionWindowRef.current.removeEventListener("pointerdown", onPointerDown);
                    regionWindowRef.current.removeEventListener("pointermove", onPointerMove);
                    regionWindowRef.current.removeEventListener("pointerup", onPointerUp);

                    regionWindowSizerRef.current.removeEventListener("pointerdown", regionWindowSizerPointerDown);
                    regionWindowSizerRef.current.removeEventListener("pointermove", regionWindowSizerPointerMove);
                    regionWindowSizerRef.current.removeEventListener("pointerup", regionWindowSizerPointerUp);

                    contentOvershotAreaRef.current.removeEventListener("pointerdown", onOvershotPointerDown);
                    contentOvershotAreaRef.current.removeEventListener("pointerup", onOvershotPointerUp);
                    contentUndershotAreaRef.current.removeEventListener("pointerdown", onUndershotPointerDown);
                    contentUndershotAreaRef.current.removeEventListener("pointerup", onUndershotPointerUp);

                    contentContainerRef.current.removeEventListener("pointerdown", onContentContainerPointerDown);

                    cleanupResizeObserver?.();
                }
            };
        }
    }, [])


    // triggers when either the zoom level changes (by regionWidthPx, initialRegionWindowLeftPx, initialScrollLeftPx) or the screen width resizes
    useEffect(() => {
        if (contentContainerRef.current && regionWidthPx) {

            minRegionWindowXRef.current = contentContainerRef.current.offsetLeft;
            maxRegionWindowXRef.current = (contentContainerRef.current.offsetWidth - regionWidthPx) + contentContainerRef.current.offsetLeft;

            setRegionWindowWidth(regionWidthPx);

            const scrollLeft = contentContainerRef.current?.scrollLeft;

            if (initialScrollLeftPx && scrollLeft != initialScrollLeftPx) {
                contentContainerRef.current.scrollTo(initialScrollLeftPx, 0);
            }

            let rw_left = initialRegionWindowLeftPx ? initialRegionWindowLeftPx + minRegionWindowXRef.current - scrollLeft : minRegionWindowXRef.current;

            if (rw_left > maxRegionWindowXRef.current) {
                const newScrollPosition = contentContainerRef.current.scrollLeft + (rw_left - maxRegionWindowXRef.current);
                contentContainerRef.current.scrollTo(newScrollPosition, 0);

                rw_left = maxRegionWindowXRef.current;
            }

            setRegionWindowLeft(rw_left);
        }
    }, [regionScrollerWidth, regionWidthPx, initialScrollLeftPx]); 

    return (
        <div className="region-scroller-container" >
            <div className="content-allowance-container">
                <div className="region-spill-area undershot fit" ref={contentUndershotAreaRef}>
                    <ArrowLeft width={21.64} height={32} viewBox="0 0 21.64 32" />
                </div>
                <div className="content-container fill" ref={contentContainerRef}>
                    {children}
                </div>
                <div className="region-spill-area overshot fit" ref={contentOvershotAreaRef}>
                    <ArrowRight width={21.64} height={32} viewBox="0 0 21.64 32" />
                </div>
            </div>
            <div className="region-window" ref={regionWindowRef}
                style={({
                    "--regionWindowWidth": `${regionWindowWidth}px`,
                    "--regionWindowLeft": `${regionWindowLeft}px`
                } as React.CSSProperties)}>
                <div className="region-window-title-container">
                    <span className="region-window-title-text">{regionWindowPositionTitle}</span>
                    <span className="region-title-indicator"></span>
                </div>
                <div className="region-window-inner fill">
                    <span>{regionWindowInteriorTitle}</span>
                </div>
                <div className={`region-window-sizer fit ${isResizeEnabled ? "enabled" : "disabled"}`} ref={regionWindowSizerRef}>
                    <span />
                </div>
            </div>
        </div>
    );
};
